import { apiClient } from '@/lib/api/client';
import type {
  LoginCredentials,
  RegisterCredentials,
  ForgotPasswordCredentials,
  ConfirmForgotPasswordCredentials,
  ConfirmSignUpCredentials,
} from '../types';

export const authApi = {
  /**
   * AWS Cognito Login via BFF endpoint.
   * Sends user credentials to Django, which authenticates against Cognito
   * and sets secure HttpOnly session cookies.
   */
  loginCognito: async (credentials: LoginCredentials) => {
    const response = await apiClient.post('/api/auth/cognito/login/', credentials);
    return response.data;
  },

  /**
   * AWS Cognito Logout via BFF endpoint.
   * Clears Django session and deletes HttpOnly authentication cookies.
   */
  logoutCognito: async () => {
    const response = await apiClient.post('/api/auth/cognito/logout/');
    return response.data;
  },

  /**
   * AWS Cognito Session Probe via BFF endpoint.
   * Verifies current session status and returns authenticated user details.
   */
  getSessionCognito: async () => {
    const response = await apiClient.get('/api/auth/cognito/session/', { skipAuthRefresh: true });
    return response.data;
  },

  /**
   * Request 6-digit OTP code to initiate password recovery via SES.
   */
  forgotPasswordCognito: async (credentials: ForgotPasswordCredentials) => {
    const response = await apiClient.post('/api/auth/cognito/forgot-password/', credentials);
    return response.data;
  },

  /**
   * Confirm password reset with 6-digit OTP code and set new password.
   */
  confirmForgotPasswordCognito: async (credentials: ConfirmForgotPasswordCredentials) => {
    const response = await apiClient.post('/api/auth/cognito/confirm-forgot-password/', credentials);
    return response.data;
  },

  /**
   * Confirm account registration with 6-digit code sent by Cognito via email.
   */
  confirmSignUpCognito: async (credentials: ConfirmSignUpCredentials) => {
    const response = await apiClient.post('/api/auth/cognito/confirm/', credentials);
    return response.data;
  },

  /**
   * Legacy SimpleJWT login endpoint (maintained for migration fallback).
   */
  login: async (credentials: LoginCredentials) => {
    const response = await apiClient.post('/api/auth/token/', credentials);
    return response.data;
  },

  register: async (credentials: RegisterCredentials) => {
    const response = await apiClient.post('/api/auth/register/', credentials);
    return response.data;
  },

  logout: async () => {
    const response = await apiClient.post('/api/auth/logout/');
    return response.data;
  },
};

